/**
 * Adverse media service — search for negative news coverage via `GET search/media`.
 *
 * Uses the 3.2 path exclusively. Each result is a news article with title,
 * description, url, media_source, individuals, entities, keywords, and country.
 *
 *   GET search/media?name=&keyword=&country=&language=&page=
 *     -> AdverseMediaEnvelope (Paginated<AdverseMediaResultRaw> + search.id)
 *
 * See ADR-010 — Service Layer Designed Fresh.
 */

import { api } from "./base_service";
import { apiEndpoints } from "@/src/config/api/endpoints";
import { isoCountryName } from "@/app/(app)/_lib/iso-countries";
import {
  ADVERSE_MEDIA_KEYWORD_LABELS,
  type AdverseMediaKeyword,
  type AdverseMediaLanguage,
} from "./search_configurations_service";
import type { Match, RiskLevel } from "@/app/(app)/screening/_data/types";
import type { ApiError, Paginated, Result } from "./types";

const ADVERSE_MEDIA_VERSION = "3.2";
const PAGE_SIZE = 100;

// ---- input ----------------------------------------------------------------

export type AdverseMediaSearchInput = {
  name: string;
  keywords?: AdverseMediaKeyword[];
  /** ISO 3166-1 alpha-2 codes. */
  countries?: string[];
  /** ISO 639-1 codes (en, es, fr, de, it, pt). */
  languages?: AdverseMediaLanguage[];
  /** 1-based page number for pagination. */
  page?: number;
  manual_search?: boolean;
  linkedSearchReference?: string;
};

// ---- wire shape -----------------------------------------------------------

/** One article result as the backend emits it (3.0 path). */
export interface AdverseMediaResultRaw {
  confidence_score?: number;
  title?: string;
  description?: string;
  published_at?: string;
  entities?: string[];
  individuals?: string[];
  media_source?: { name?: string; url?: string };
  url?: string;
  image?: string[];
  country?: string;
  language?: string;
  keywords?: string[];
  si_identifier?: string;
  /** Full archived article text. */
  body?: string;
}

/** The `GET search/media` envelope: DRF list shape + search metadata. */
interface AdverseMediaEnvelope extends Paginated<AdverseMediaResultRaw> {
  search?: { id?: string } | null;
}

// ---- adapters -------------------------------------------------------------

function asStr(v: unknown): string {
  if (typeof v === "string") return v;
  if (v == null) return "";
  return String(v);
}

function riskFromScore(score: number): RiskLevel {
  if (score >= 0.9) return "critical";
  if (score >= 0.8) return "high";
  if (score >= 0.6) return "medium";
  return "low";
}

export function adaptAdverseMediaResult(raw: AdverseMediaResultRaw, searchId?: string): Match {
  const scoreRaw = raw.confidence_score ?? 0;
  const score = Number.isFinite(scoreRaw) ? scoreRaw : 0;
  const countryIso = raw.country;
  const country = countryIso ? isoCountryName(countryIso) || countryIso : "";

  const id = raw.si_identifier ?? raw.url ?? raw.title ?? "";

  const fullTitle = asStr(raw.title);
  const additionalInformation: Record<string, string> = {};
  if (fullTitle.length > 100) additionalInformation["Full Title"] = fullTitle;
  if (raw.url) additionalInformation["Article URL"] = raw.url;
  if (raw.media_source?.name) additionalInformation["Media Source"] = raw.media_source.name;
  if (raw.media_source?.url) additionalInformation["Source URL"] = raw.media_source.url;
  if (raw.keywords?.length) {
    additionalInformation["Keywords"] = raw.keywords
      .map((k) => ADVERSE_MEDIA_KEYWORD_LABELS[k] ?? k)
      .join(", ");
  }
  if (raw.individuals?.length) additionalInformation["Individuals"] = raw.individuals.join(", ");
  if (raw.entities?.length) additionalInformation["Entities"] = raw.entities.join(", ");
  if (raw.language) additionalInformation["Language"] = raw.language;
  if (raw.image?.[0]) additionalInformation["Image"] = raw.image[0];

  return {
    id,
    siIdentifier: raw.si_identifier,
    searchId,
    score,
    name: fullTitle.length > 100 ? `${fullTitle.slice(0, 100)}...` : fullTitle,
    type: "Media",
    country,
    countryIso,
    dob: null,
    source: "ADV-MEDIA",
    collection: "adverse_media",
    lastUpdated: (raw.published_at ?? "").slice(0, 10),
    mediaSource: raw.media_source?.name || undefined,
    riskLevel: riskFromScore(score),
    alternativeNames: [],
    knownLocations: country ? [{ label: "Country", value: country }] : [],
    remarks: raw.description ? [raw.description] : [],
    additionalInformation,
    articleBody: raw.body || undefined,
  };
}

// ---- query builder --------------------------------------------------------

function buildParams(input: AdverseMediaSearchInput): Record<string, string | number> {
  const params: Record<string, string | number> = {
    name: input.name,
    page_size: PAGE_SIZE,
  };
  if (input.keywords && input.keywords.length > 0) {
    params.keyword = input.keywords.join(",");
  }
  if (input.countries && input.countries.length > 0) {
    params.country = input.countries.join(",");
  }
  if (input.languages && input.languages.length > 0) {
    params.language = input.languages.join(",");
  }
  if (input.page && input.page > 1) {
    params.page = input.page;
  }
  if (input.manual_search) {
    params.manual_search = "true";
  }
  if (input.linkedSearchReference) {
    params.linked_search_reference = input.linkedSearchReference;
  }
  return params;
}

// ---- public API -----------------------------------------------------------

/**
 * Search for adverse media coverage of a name.
 * GET search/media?name=&keyword=&country=&language=&page=
 *
 * Results are sorted by descending confidence score. The `signal` lets the
 * caller abort a superseded search (same pattern as searchByName).
 */
export async function searchAdverseMedia(
  input: AdverseMediaSearchInput,
  signal?: AbortSignal,
): Promise<
  Result<{ results: Match[]; count: number; next: string | null; previous: string | null }>
> {
  try {
    const data = await api.get<AdverseMediaEnvelope>(apiEndpoints.adverseMedia().search(), {
      params: buildParams(input),
      version: ADVERSE_MEDIA_VERSION,
      signal,
    });
    const searchId = data.search?.id ? String(data.search.id) : undefined;
    const results = (data.results ?? [])
      .map((raw) => adaptAdverseMediaResult(raw, searchId))
      .sort((a, b) => b.score - a.score);
    return {
      ok: true,
      value: {
        results,
        count: data.count ?? results.length,
        next: data.next ?? null,
        previous: data.previous ?? null,
      },
    };
  } catch (err) {
    return { ok: false, error: err as ApiError };
  }
}
